import { useState } from 'react'
import { Button } from '@/common/ui/button'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '@/common/ui/dialog'
import { GetGameListTableData } from '@/modules/game/type'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import { Edit } from 'lucide-react'
import UploadImage from '@/common/components/UploadImage'
import useMutationCreateGameImageFileUrl from '@/modules/game/api/hooks/useMutationCreateGameImageFileUrl'
import useMutationUpdateGame from '@/modules/game/api/hooks/useMutationUpdateGame'
import { useQueryClient } from '@tanstack/react-query'
import { GameQueryKeys } from '@/modules/game/keys'

interface EditGameImageProps {
	row: GetGameListTableData
}

export default function EditGameImage({ row }: EditGameImageProps) {
	const [open, setOpen] = useState(false)
	const [imageUrl, setImageUrl] = useState(row.imageUrl || '')
	const { mutate: updateGame } = useMutationUpdateGame()
	const { mutateAsync: getGameImageFileUrl } =
		useMutationCreateGameImageFileUrl()
	const queryClient = useQueryClient()

	const handleImageUploaded = (fileUrl: string) => {
		setImageUrl(fileUrl)
	}

	const handleSave = () => {
		if (!imageUrl) {
			showErrorToast('กรุณาเลือกรูปภาพ')
			return
		}

		updateGame(
			{
				id: row.id,
				payload: {
					imageUrl: imageUrl,
				},
			},
			{
				onSuccess: () => {
					toast.success('อัพเดทรูปภาพเกมสำเร็จ')
					queryClient.invalidateQueries({
						queryKey: [GameQueryKeys.GetGameList],
					})
					setOpen(false)
				},
				onError: (error) => {
					showErrorToast(error.message || 'เกิดข้อผิดพลาด')
				},
			},
		)
	}

	const handleReset = () => {
		const externalImageUrl = (row as any).externalImageUrl
		if (!externalImageUrl) {
			showErrorToast('ไม่มีรูปภาพเริ่มต้นสำหรับเกมนี้')
			return
		}

		updateGame(
			{
				id: row.id,
				payload: {
					imageUrl: externalImageUrl,
				},
			},
			{
				onSuccess: () => {
					toast.success('คืนค่ารูปภาพเป็นค่าเริ่มต้นสำเร็จ')
					queryClient.invalidateQueries({
						queryKey: [GameQueryKeys.GetGameList],
					})
					setOpen(false)
				},
				onError: (error) => {
					showErrorToast(error.message || 'เกิดข้อผิดพลาด')
				},
			},
		)
	}

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogTrigger asChild>
				<Button variant="ghost" size="sm">
					<Edit className="h-4 w-4" />
				</Button>
			</DialogTrigger>
			<DialogContent className="max-w-md">
				<DialogHeader>
					<DialogTitle>
						แก้ไขรูปภาพเกม: {row.displayName || row.name}
					</DialogTitle>
				</DialogHeader>
				<div className="space-y-4">
					<div className="grid grid-cols-2 gap-4">
						<div className="space-y-2">
							<label className="text-sm font-medium">รูปภาพปัจจุบัน</label>
							{row.imageUrl ? (
								<div className="h-42 w-32 overflow-hidden rounded border">
									<img
										src={row.imageUrl}
										alt={row.displayName || row.name}
										className="h-full w-full object-cover"
									/>
								</div>
							) : (
								<div className="h-42 flex w-32 items-center justify-center rounded border bg-gray-200 text-xs text-gray-500">
									ไม่มีรูปภาพ
								</div>
							)}
						</div>

						<div className="space-y-2">
							<label className="text-sm font-medium">รูปภาพเริ่มต้น</label>
							{(row as any).externalImageUrl ? (
								<div className="h-42 w-32 overflow-hidden rounded border">
									<img
										src={(row as any).externalImageUrl}
										alt={`${row.displayName || row.name} (เริ่มต้น)`}
										className="h-full w-full object-cover"
									/>
								</div>
							) : (
								<div className="h-42 flex w-32 items-center justify-center rounded border bg-gray-200 text-xs text-gray-500">
									ไม่มีรูปภาพเริ่มต้น
								</div>
							)}
						</div>
					</div>
					<div className="space-y-2">
						<label className="text-sm font-medium">
							อัพโหลดรูปภาพใหม่{' '}
							<span className="text-amber-600 dark:text-amber-400">
								(สัดส่วน 130:173)
							</span>
						</label>
						<UploadImage
							onImageUploaded={handleImageUploaded}
							defaultValue={imageUrl}
							getFileUrl={() =>
								getGameImageFileUrl({
									payload: { fileExtension: '' },
								})
							}
							aspectRatio="130/173"
							previewHeight={173}
						/>
					</div>
					<div className="flex justify-between">
						<Button
							variant="outline"
							onClick={handleReset}
							disabled={!(row as any).externalImageUrl}
						>
							คืนค่าเริ่มต้น
						</Button>
						<div className="flex gap-2">
							<Button variant="outline" onClick={() => setOpen(false)}>
								ยกเลิก
							</Button>
							<Button onClick={handleSave}>บันทึก</Button>
						</div>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	)
}
